import { useEffect, useState } from 'react';
import { listLocalGames, type LocalGameRecord } from '~/store/localGames.js';
import { playerId as currentPlayerId, useAuthStore, usePlayerId } from '~/store/authStore.js';
import { markCrossTourDone, useSettingsStore } from '~/store/settingsStore.js';
import { RESUME_DEADLINE_MS, type ResumeCheck } from '~/ui/ResumeDoor.js';
import { useLatestRequest } from '~/ui/useLatestRequest.js';

/**
 * Whether to invite this player to learn Cross-Cribbage.
 *
 * The invitation is for someone who plays regular cribbage well and has never
 * met the grid: ten or more finished regular games, five or more won, no
 * finished Cross game, the walkthrough never completed, and no game in
 * progress. The server answers the first four from its own records
 * (`me.cross_invitation`). This adds what only the device knows:
 *
 * - The walkthrough finished here, before the server heard about it.
 * - A Cross game finished on the device that the server was never sent:
 *   pass-and-play, or a bot game with sync turned off.
 * - A game in progress. It is the Resume door's question, so the caller asks
 *   it the Resume door's way and passes the whole check in: a screen with no
 *   door calls `useResumeCheck({ poll: false })`, and a screen with one passes
 *   the check its door reads. The hook waits for `answered`, never `settled`,
 *   which the home's deadline also sets, so one screen can never show both.
 *   It takes the check rather than a boolean because the first caller gated
 *   on the wrong flag (GPT review F11, 2026-10-01).
 *
 * `show` is false until the device has answered, so the invitation never
 * flashes on and then off. `settled` says the answer is in, for a screen that
 * changes around it: the home's Play door goes white while the invitation
 * shows, so the home waits for `settled` the way it waits for the Resume
 * check's. A device read that never answers settles anyway, at the Resume
 * check's deadline, with no invitation yet. An answer that lands after the
 * deadline can still bring the invitation in, and Play goes white then, the
 * same way a late Resume door arrives. The cached `me` is as old as the last
 * launch, Profile or Stats visit, or accepted import, so a player becomes
 * eligible on their next launch rather than mid-session. See
 * `docs/dev/cross_invitation.md`.
 */
export function useCrossInvitation({
  resumeCheck,
}: {
  readonly resumeCheck: ResumeCheck;
}): CrossInvitation {
  const player = usePlayerId();
  const invited = useAuthStore((state) => state.user?.cross_invitation ?? false);
  const tourDone = useSettingsStore(
    (settings) => player !== null && settings.crossTourPlayerIds.includes(player),
  );
  const [device, setDevice] = useState<{ readonly player: string; readonly clear: boolean }>();
  const [late, setLate] = useState(false);
  const request = useLatestRequest();

  useEffect(() => {
    const deadline = setTimeout(() => {
      setLate(true);
    }, RESUME_DEADLINE_MS);
    return () => {
      clearTimeout(deadline);
    };
  }, []);

  useEffect(() => {
    if (!invited || tourDone || player === null) return;
    const isCurrent = request.begin();
    void listLocalGames(player, Number.MAX_SAFE_INTEGER).then((games) => {
      if (isCurrent()) setDevice({ player, clear: deviceAllowsInvitation(games) });
    });
    return request.retire;
  }, [request, invited, tourDone, player]);

  // Finished on this device, and the server still says otherwise: the report
  // never landed. Idempotent on the server, so sending it again is safe.
  useEffect(() => {
    if (invited && tourDone) void useAuthStore.getState().completeCrossTour();
  }, [invited, tourDone]);

  const quiet = !invited || tourDone || player === null;
  const deviceAnswered = device?.player === player;
  const noGameWaiting = resumeCheck.answered && resumeCheck.resume === null;
  return {
    show: !quiet && noGameWaiting && deviceAnswered && device.clear,
    settled:
      quiet || late || (resumeCheck.settled && (resumeCheck.resume !== null || deviceAnswered)),
  };
}

/** What `useCrossInvitation` answers. */
export interface CrossInvitation {
  /** Draw the invitation. */
  readonly show: boolean;
  /** `show` is this visit's answer, so a screen that changes around it can draw. */
  readonly settled: boolean;
}

/**
 * The device's half of the rule: no finished Cross game among this player's
 * games on the device.
 *
 * Exported because it is the rule worth testing, and it is testable without a
 * database.
 */
export function deviceAllowsInvitation(games: readonly LocalGameRecord[]): boolean {
  return !games.some((game) => game.config.variant === 'cross' && game.result !== null);
}

/**
 * The walkthrough is finished: the Cross lesson played to its game over
 * (Mitch, 2026-09-30). Leaving the lesson early does not count. Remembered on
 * the device at once, then sent to the server.
 */
export function finishCrossTour(): void {
  const player = currentPlayerId();
  if (player !== null) markCrossTourDone(player);
  void useAuthStore.getState().completeCrossTour();
}
